import type { Issue } from './types';

/**
 * HOW A CARD'S DATES HAVE MOVED — the read behind migration 0125.
 *
 * A card's target date (`dueDate`) and start date (`startsOn`) each carry, from
 * the database, a BASELINE (the day it was first set) and a PUSH COUNT (how many
 * times it has moved later). This module turns those raw fields into the one
 * thing every surface wants: the slip — how far the date has drifted from where
 * it started — with the count beside it. It computes; it renders nothing.
 *
 * "Live" gates the loud surfaces (the card face, the backlog token): a date that
 * moves while the card is still waiting to be started is ordinary re-planning,
 * not slippage. Slippage is a plan drifting UNDER the work, so it counts only
 * once the work has begun.
 */

/** Whole days from one `YYYY-MM-DD` day to another, read at local midnight so a
 *  timezone cannot shift the count by one. Positive means `to` is the later day. */
export function dayGap(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  if (!fy || !fm || !fd || !ty || !tm || !td) return 0;
  const a = new Date(fy, fm - 1, fd).getTime();
  const b = new Date(ty, tm - 1, td).getTime();
  return Math.round((b - a) / 86_400_000);
}

export interface DateSlip {
  /** The date as it stands now (`YYYY-MM-DD`). */
  current: string;
  /** The day it was first set (`YYYY-MM-DD`), or null if that is unknown. */
  baseline: string | null;
  /** current − baseline in days: positive = drifted later, negative = pulled in,
   *  0 = unmoved. Null when there is no baseline to measure against. */
  slipDays: number | null;
  /** How many times this date has moved LATER. */
  pushes: number;
  /** Whether the date has drifted from its baseline at all (either direction). */
  moved: boolean;
}

/** The slip for one date, or null when the card has no such date set. */
export function dateSlip(
  current: string | null | undefined,
  baseline: string | null | undefined,
  pushes: number | null | undefined,
): DateSlip | null {
  if (!current) return null;
  const base = baseline ?? null;
  const slipDays = base ? dayGap(base, current) : null;
  return {
    current,
    baseline: base,
    slipDays,
    pushes: pushes ?? 0,
    moved: slipDays != null && slipDays !== 0,
  };
}

/** The target-date slip for a card. */
export const dueSlip = (i: Issue): DateSlip | null =>
  dateSlip(i.dueDate, i.dueDateBaseline, i.dueDatePushes);

/** The start-date slip for a card. */
export const startSlip = (i: Issue): DateSlip | null =>
  dateSlip(i.startsOn, i.startsOnBaseline, i.startsOnPushes);

/**
 * WHETHER A CARD IS LIVE — work has begun and is not finished. `startedAt` is
 * stamped when a card first moves into progress (migration 0096), so it is the
 * honest "the work is underway" test, and a resolved card (done / won't-do) is
 * never live however it was stamped.
 */
export function isLiveCard(i: Issue): boolean {
  const resolved = i.status === 'done' || i.status === 'wontdo';
  return !resolved && i.startedAt != null;
}

/** The number the face mark and the backlog token key off — the target date's
 *  later-push count, but only while the card is live (0 otherwise). */
export function targetPushes(i: Issue): number {
  return isLiveCard(i) ? i.dueDatePushes ?? 0 : 0;
}

/** How many later pushes make a card face wear the mark. Two: one push is a
 *  correction, a pattern is what a board should be able to see at a glance. */
export const PUSH_MARK_THRESHOLD = 2;

/** The signed slip as a short label — "+14d" later, "−5d" pulled in. */
export function slipLabel(slipDays: number): string {
  return slipDays > 0 ? `+${slipDays}d` : `−${-slipDays}d`;
}
